/** @format */
/** @format */

import React, { FC, useState, useMemo } from 'react'
import styled from 'styled-components'
import { Drawer, Row, Col } from '@king-design/react'
import { useAsync } from 'react-use'
import { CloseFooter, TablePage } from 'src/components'
import Api from 'src/api/ApiCloud'
import { request } from 'src/utils'
import { nodeStatus, portStatus, portStatusColor, handleUsageNum } from './constants'

interface NodeDrawerProps {
  className?: string
  visible: boolean
  onClose: () => void
  dataSource: string
  nodeData: any
}

const initTableGroup = {
  AdminStatus: '',
  OperStatus: ''
}

const ClassNameEnum = {
  EnterpriseSwitch: '交换机',
  Firewall: '防火墙',
  EnterpriseRouter: '路由器'
}

const NodeDrawer: FC<NodeDrawerProps> = ({ className, visible, onClose, dataSource, nodeData }) => {
  const [tableGroup, setTableGroup] =
    useState<{ AdminStatus: string; OperStatus: string }>(initTableGroup)
  const [page, setPage] = useState<{ limit: number; value: number }>({
    value: 1,
    limit: 10
  })

  // 获取接口数据
  const { value: port_value, loading: port_loading } = useAsync(async () => {
    if (visible && nodeData?.id) {
      const result = await request(Api.getNCEPort, {
        data: { DataSource: dataSource, NeDN: nodeData?.id }
      })
      return result?.data?.ListPortsResult?.Data || []
    } else {
      return []
    }
  }, [nodeData, visible])

  // 筛选接口
  const tableData = useMemo(() => {
    if (port_value?.length) {
      const { AdminStatus, OperStatus } = tableGroup
      return port_value.filter(item => {
        return (
          (!AdminStatus || parseInt(AdminStatus) === item.AdminStatus) &&
          (!OperStatus || parseInt(OperStatus) === item.OperStatus)
        )
      })
    } else {
      return []
    }
  }, [port_value, tableGroup])

  const scheme = {
    Name: {
      title: '名称'
    },
    AdminStatus: {
      title: '管理状态',
      width: 200,
      group: [
        { label: '全部', value: '' },
        ...Object.keys(portStatus).map(key => ({ label: portStatus[key], value: key }))
      ],
      template: ({ AdminStatus }) => (
        <span className={`port_status port_status_${AdminStatus}`}>
          {portStatus[AdminStatus] || '-'}
        </span>
      )
    },
    OperStatus: {
      title: '操作状态',
      width: 200,
      group: [
        { label: '全部', value: '' },
        ...Object.keys(portStatus).map(key => ({ label: portStatus[key], value: key }))
      ],
      template: ({ OperStatus }) => (
        <span className={`port_status port_status_${OperStatus}`}>
          {portStatus[OperStatus] || '-'}
        </span>
      )
    }
  }

  return (
    <div id="node_drawer">
      <Drawer
        value={visible}
        width={600}
        container={() => document.getElementById('node_drawer')}
        slotFooter={<CloseFooter onClose={onClose} />}
        onChangeValue={show => {
          if (!show) {
            setTableGroup(initTableGroup)
            setPage({
              value: 1,
              limit: 10
            })
            onClose()
          }
        }}
        title="设备详情"
      >
        <div className={className}>
          <p className="title" style={{ marginTop: 0 }}>
            设备信息
          </p>
          <Row gutter="16" className="cable_info">
            <Col span="12">
              <p>
                名称<span>{nodeData?.param?.NeName || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                状态<span>{nodeStatus[nodeData?.param?.Status] || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                分类<span>{ClassNameEnum[nodeData?.param?.ClassName] || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                角色<span>{nodeData?.param?.FabricNeRoles || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                管理IP<span>{nodeData?.param?.NeIP || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                MAC<span>{nodeData?.param?.MAC || ''}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                型号
                <span>
                  {nodeData?.param?.NeType.replace(`{${nodeData?.param?.Manufacturer}}`, '') || ''}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                版本<span>{nodeData?.param?.Version || 0}</span>
              </p>
            </Col>
            <Col span="12">
              <p>
                厂商<span>{nodeData?.param?.Manufacturer || ''}</span>
              </p>
            </Col>
          </Row>
          <p className="title">指标信息</p>
          <Row gutter="16" className="cable_info">
            <Col span="12">
              <p>
                CPU利用率
                <span>
                  {nodeData?.param?.PMIndicator?.cpu_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.cpu_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                内存利用率
                <span>
                  {nodeData?.param?.PMIndicator?.mem_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.mem_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                MAC利用率
                <span>
                  {nodeData?.param?.PMIndicator?.mac_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.mac_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                ARP利用率
                <span>
                  {nodeData?.param?.PMIndicator?.arp_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.arp_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                ND利用率
                <span>
                  {nodeData?.param?.PMIndicator?.nd_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.nd_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                FIBv4利用率
                <span>
                  {nodeData?.param?.PMIndicator?.fibv4_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.fibv4_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
            <Col span="12">
              <p>
                FIBv6利用率
                <span>
                  {nodeData?.param?.PMIndicator?.fibv6_usage !== undefined
                    ? `${handleUsageNum(nodeData?.param?.PMIndicator?.fibv6_usage)}% `
                    : '-'}
                </span>
              </p>
            </Col>
          </Row>
          <p className="title">接口信息</p>
          <TablePage
            tableProps={{
              loading: port_loading,
              key: 'PortID',
              data: tableData,
              checkType: 'none',
              scheme,
              group: tableGroup,
              onChangeGroup: group => {
                setTableGroup(group)
                setPage({ value: 1, limit: page.limit })
              },
              pagination: {
                total: tableData?.length,
                ...page
              },
              onPage: ({ value, limit }) => {
                setPage({ value, limit })
              }
            }}
            showPage={false}
          ></TablePage>
        </div>
      </Drawer>
    </div>
  )
}

export default styled(NodeDrawer)`
  .title {
    margin-top: 16px;
    padding: 12px 20px 12px 20px;
    height: 52px;
    line-height: 28px;
    background: #f8f8fa;
    color: #3c4449;
    font-size: 14px;
  }
  .cable_info {
    p {
      padding: 8px 0;
      margin: 0;
      color: #848f9a;
      span {
        margin-left: 8px;
        color: #3c4449;
      }
    }
  }
  .port_status {
    &::before {
      content: '';
      border-radius: 50%;
      display: inline-block;
      height: 8px;
      width: 8px;
      margin-right: 6px;
      position: relative;
      top: 1px;
    }
    &.port_status_11::before {
      background: ${portStatusColor[11]};
    }
    &.port_status_12::before {
      background: ${portStatusColor[12]};
    }
  }
`
